import React from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/AppContext";
import { Lightning, Newspaper, MapPin, BookOpen, ShieldCheck, TiktokLogo, FacebookLogo, InstagramLogo, YoutubeLogo, XLogo, UsersThree } from "@phosphor-icons/react";

const SOCIALS = [
  { name: "TikTok", href: "https://www.tiktok.com/@chambanow", Icon: TiktokLogo },
  { name: "Instagram", href: "https://www.instagram.com/chamba_now/", Icon: InstagramLogo },
  { name: "Facebook", href: "https://www.facebook.com/profile.php?id=61591105658108", Icon: FacebookLogo },
  { name: "YouTube", href: "https://www.youtube.com/@ChambaNOW", Icon: YoutubeLogo },
  { name: "X", href: "https://x.com/Chamba_NOW", Icon: XLogo },
];

export default function Footer() {
  const { t, lang, user, hideChrome } = useApp();
  const isHirer = user?.role === "hirer";

  if (hideChrome) return null;
  return (
    <footer className="border-t border-white/10 bg-[#0A0A0A] mt-24">
      {/* Compact city directory links (replaced the two 200-city walls to keep
          conversion pages focused; /cities still links out to every city for SEO). */}
      <div className="border-b border-white/5" data-testid="footer-cities">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex flex-wrap items-center gap-x-6 gap-y-2">
          <span className="flex items-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-[#FF7A1A]">
            <MapPin size={12} weight="fill" /> {lang === "es" ? "Por ciudad" : "By city"}
          </span>
          <Link to="/cities" className="text-xs font-semibold text-zinc-300 hover:text-[#FF7A1A] transition-colors" data-testid="footer-more-cities-jobs">
            {lang === "es" ? "Buscar trabajo por ciudad" : "Find work by city"} &rarr;
          </Link>
          <Link to="/cities" className="text-xs font-semibold text-zinc-300 hover:text-[#FF7A1A] transition-colors" data-testid="footer-more-cities-hire">
            {lang === "es" ? "Contratar por ciudad" : "Hire workers by city"} &rarr;
          </Link>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 grid md:grid-cols-3 gap-8">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#FF7A1A] flex items-center justify-center">
              <Lightning size={18} weight="fill" color="#fff" />
            </div>
            <span className="font-display text-xl tracking-tight text-white">CHAMBANOW</span>
          </div>
          <p className="mt-3 text-zinc-500 text-sm">{t.footer.tagline}</p>
          {isHirer && (
            <div className="mt-5" data-testid="footer-hirer-links">
              <p className="font-bold uppercase tracking-[0.15em] text-[10px] text-zinc-500 mb-2">{lang === "es" ? "Mi cuenta" : "My account"}</p>
              <div className="flex flex-col gap-1.5 text-sm">
                <Link to="/pricing" className="text-[#FF7A1A] hover:text-[#ff8b35] underline decoration-dotted underline-offset-2 transition-colors" data-testid="footer-plans-link">
                  {t.nav.plans}
                </Link>
                <Link to="/referrals" className="text-[#FF7A1A] hover:text-[#ff8b35] underline decoration-dotted underline-offset-2 transition-colors" data-testid="footer-referrals-link">
                  {t.referral.navLink}
                </Link>
              </div>
            </div>
          )}
        </div>
        <div className="text-sm text-zinc-500">
          <p className="font-bold uppercase tracking-[0.15em] text-xs text-zinc-300 mb-2">chambanow.com</p>
          <p>© {new Date().getFullYear()} ChambaNow. {t.footer.rights}</p>
          <Link
            to="/press"
            className="inline-flex items-center gap-1.5 mt-3 text-[#FF7A1A] hover:text-[#ff8b35] underline decoration-dotted underline-offset-2 transition-colors"
            data-testid="footer-press-link"
          >
            <Newspaper size={14} weight="bold" /> {t.footer.press}
          </Link>
          <div className="mt-2">
            <Link
              to="/about"
              className="inline-flex items-center gap-1.5 text-[#FF7A1A] hover:text-[#ff8b35] underline decoration-dotted underline-offset-2 transition-colors text-sm"
              data-testid="footer-about-link"
            >
              <UsersThree size={14} weight="bold" /> {lang === "es" ? "Quiénes somos" : "About us"}
            </Link>
          </div>
          <div className="mt-2">
            <Link
              to="/chambamatters"
              className="inline-flex items-center gap-1.5 text-[#FF7A1A] hover:text-[#ff8b35] underline decoration-dotted underline-offset-2 transition-colors text-sm"
              data-testid="footer-chambamatters-link"
            >
              <BookOpen size={14} weight="bold" /> Chamba Matters
            </Link>
          </div>
          <div className="mt-2">
            <Link
              to="/worker-resources"
              className="inline-flex items-center gap-1.5 text-[#FF7A1A] hover:text-[#ff8b35] underline decoration-dotted underline-offset-2 transition-colors text-sm"
              data-testid="footer-worker-resources-link"
            >
              <ShieldCheck size={14} weight="bold" /> {lang === "es" ? "Recursos para Trabajadores" : "Worker Resources"}
            </Link>
          </div>
          <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs">
            <Link to="/terms" className="text-zinc-400 hover:text-[#FF7A1A] underline decoration-dotted underline-offset-2 transition-colors" data-testid="footer-terms-link">
              {t.footer.terms}
            </Link>
            <span className="text-zinc-700">·</span>
            <Link to="/privacy" className="text-zinc-400 hover:text-[#FF7A1A] underline decoration-dotted underline-offset-2 transition-colors" data-testid="footer-privacy-link">
              {t.footer.privacy}
            </Link>
            <span className="text-zinc-700">·</span>
            <Link to="/sms-terms" className="text-zinc-400 hover:text-[#FF7A1A] underline decoration-dotted underline-offset-2 transition-colors" data-testid="footer-sms-terms-link">
              {lang === "es" ? "Términos SMS" : "SMS Terms"}
            </Link>
          </div>
          <div className="mt-3">
            <p className="font-bold uppercase tracking-[0.15em] text-[10px] text-zinc-500 mb-1">{lang === "es" ? "Comparar" : "Compare"}</p>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
              <Link to="/vs/craigslist" className="text-zinc-400 hover:text-[#FF7A1A] underline decoration-dotted underline-offset-2 transition-colors" data-testid="footer-vs-craigslist">vs Craigslist</Link>
              <span className="text-zinc-700">·</span>
              <Link to="/vs/instawork" className="text-zinc-400 hover:text-[#FF7A1A] underline decoration-dotted underline-offset-2 transition-colors" data-testid="footer-vs-instawork">vs Instawork</Link>
              <span className="text-zinc-700">·</span>
              <Link to="/vs/staffing-agency" className="text-zinc-400 hover:text-[#FF7A1A] underline decoration-dotted underline-offset-2 transition-colors" data-testid="footer-vs-agency">vs {lang === "es" ? "Agencias" : "Staffing agency"}</Link>
            </div>
          </div>
        </div>
        <div className="text-sm text-zinc-500 md:text-right">
          <p className="font-bold uppercase tracking-[0.15em] text-xs text-zinc-300 mb-2">{t.footer.contact}</p>
          <p>hello@chambanow.com</p>
          <div className="mt-5">
            <p className="font-bold uppercase tracking-[0.15em] text-xs text-zinc-300 mb-2">{lang === "es" ? "Síguenos" : "Follow Us"}</p>
            <div className="flex gap-3 md:justify-end" data-testid="footer-socials">
              {SOCIALS.map(({ name, href, Icon }) => (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={name}
                  className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#FF7A1A] text-zinc-300 hover:text-white flex items-center justify-center transition-colors"
                  data-testid={`footer-social-${name.toLowerCase()}`}
                >
                  <Icon size={20} weight="fill" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
